import React, { useState } from 'react';
import {
  Mail, Server, Shield, CheckCircle, AlertTriangle, XCircle, Info,
  Search, ExternalLink, RefreshCw, AlertCircle
} from 'lucide-react';
import axios from 'axios';

interface DiagnosticCheck {
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  record?: string;
  records?: string[];
  selectors?: Array<{ selector: string; record: string; status: string }>;
  issues?: string[];
  solution?: string;
  error?: string;
  postfix?: string;
  dovecot?: string;
  ip?: string;
  checkUrls?: string[];
}

interface DiagnosticResult {
  domain: string;
  timestamp: string;
  checks: {
    mx?: DiagnosticCheck;
    spf?: DiagnosticCheck;
    dmarc?: DiagnosticCheck;
    dkim?: DiagnosticCheck;
    mailServer?: DiagnosticCheck;
    blacklist?: DiagnosticCheck;
  };
  summary: {
    status: 'healthy' | 'warning' | 'critical';
    totalChecks: number;
    errors: number;
    warnings: number;
    message: string;
  };
}

const MailDiagnosticsPage: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async () => {
    if (!domain.trim()) {
      setError('Lütfen bir domain girin');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        '/api/mail-diagnostics/check',
        { domain: domain.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.error || 'Bir hata oluştu');
      }
    } catch (err: any) {
      console.error('Mail diagnostics error:', err);
      setError(err.response?.data?.error || 'Sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-primary" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-primary/10 border-primary/30';
      default:
        return 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-700';
    }
  };

  const getSummaryColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
    }
  };

  const renderCheck = (title: string, icon: React.ReactNode, check?: DiagnosticCheck) => {
    if (!check) return null;

    return (
      <div className={`border rounded-lg p-4 ${getStatusColor(check.status)}`}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            {icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
              {getStatusIcon(check.status)}
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{check.message}</p>

            {check.record && (
              <div className="bg-white dark:bg-gray-800 bg-opacity-50 rounded p-2 mb-2">
                <code className="text-xs text-gray-800 dark:text-gray-200 break-all">{check.record}</code>
              </div>
            )}

            {check.records && check.records.length > 0 && (
              <div className="bg-white dark:bg-gray-800 bg-opacity-50 rounded p-2 mb-2">
                {check.records.map((record, idx) => (
                  <code key={idx} className="text-xs text-gray-800 dark:text-gray-200 block">{record}</code>
                ))}
              </div>
            )}

            {check.selectors && check.selectors.length > 0 && (
              <div className="bg-white dark:bg-gray-800 bg-opacity-50 rounded p-2 mb-2">
                {check.selectors.map((sel, idx) => (
                  <div key={idx} className="mb-1">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{sel.selector}:</span>
                    <code className="text-xs text-gray-600 dark:text-gray-400 ml-2 break-all">{sel.record}</code>
                  </div>
                ))}
              </div>
            )}

            {check.postfix && (
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-semibold">Postfix:</span> {check.postfix}
              </div>
            )}

            {check.dovecot && (
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-semibold">Dovecot:</span> {check.dovecot}
              </div>
            )}

            {check.ip && (
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-semibold">IP:</span> {check.ip}
              </div>
            )}

            {check.checkUrls && check.checkUrls.length > 0 && (
              <div className="mt-2">
                {check.checkUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-dark text-sm flex items-center space-x-1 mb-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Blacklist kontrolü {idx + 1}</span>
                  </a>
                ))}
              </div>
            )}

            {check.issues && check.issues.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Sorunlar:</p>
                <ul className="list-disc list-inside space-y-1">
                  {check.issues.map((issue, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {check.solution && (
              <div className="mt-3 bg-primary/10 border border-primary/30 rounded p-3">
                <p className="text-sm font-semibold text-primary-dark mb-1">Çözüm:</p>
                <pre className="text-xs text-primary-dark whitespace-pre-wrap">{check.solution}</pre>
              </div>
            )}

            {check.error && (
              <div className="mt-2 text-xs text-red-600">
                <span className="font-semibold">Hata:</span> {check.error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <div className="flex items-center space-x-2 mb-1">
          <Mail className="w-5 h-5 md:w-6 md:h-6 text-primary" />
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">Mail Sunucu Diagnostics</h1>
        </div>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
          Domain adınızı girerek SPF, DKIM, DMARC ve MX kayıtlarınızı kontrol edin
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex space-x-4">
          <div className="flex-1">
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Domain Adı
            </label>
            <input
              type="text"
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCheck()}
              placeholder="örn: devkit.com.tr"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleCheck}
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Kontrol Ediliyor...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Kontrol Et</span>
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary */}
          <div className={`border-2 rounded-lg p-6 ${getSummaryColor(result.summary.status)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-1">Genel Durum: {result.domain}</h2>
                <p className="text-sm">{result.summary.message}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">{result.summary.totalChecks}</div>
                <div className="text-sm">Toplam Kontrol</div>
                {result.summary.errors > 0 && (
                  <div className="text-sm text-red-600 font-semibold mt-1">
                    {result.summary.errors} Hata
                  </div>
                )}
                {result.summary.warnings > 0 && (
                  <div className="text-sm text-yellow-600 font-semibold">
                    {result.summary.warnings} Uyarı
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detailed Checks */}
          <div className="space-y-4">
            {renderCheck('MX Records (Mail Exchange)', <Server className="w-5 h-5 text-primary" />, result.checks.mx)}
            {renderCheck('SPF (Sender Policy Framework)', <Shield className="w-5 h-5 text-green-600" />, result.checks.spf)}
            {renderCheck('DKIM (DomainKeys Identified Mail)', <CheckCircle className="w-5 h-5 text-purple-600" />, result.checks.dkim)}
            {renderCheck('DMARC (Domain-based Message Authentication)', <AlertTriangle className="w-5 h-5 text-orange-600" />, result.checks.dmarc)}
            {result.checks.mailServer && renderCheck('Mail Sunucu Durumu', <Server className="w-5 h-5 text-indigo-600" />, result.checks.mailServer)}
            {result.checks.blacklist && renderCheck('Blacklist Kontrolü', <AlertCircle className="w-5 h-5 text-red-600" />, result.checks.blacklist)}
          </div>

          {/* Timestamp */}
          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            Kontrol Zamanı: {new Date(result.timestamp).toLocaleString('tr-TR')}
          </div>
        </div>
      )}
    </div>
  );
};

export default MailDiagnosticsPage;
